
import React from 'react';
import EntityDetailLayout from '../EntityDetailLayout';
import { useTransactionDetails } from './transaction/useTransactionDetails';
import TransactionLoading from './transaction/TransactionLoading';
import TransactionError from './transaction/TransactionError';
import TransactionNotFound from './transaction/TransactionNotFound';
import TransactionNetAmount from './transaction/TransactionNetAmount';
import TransactionCustomerInfo from './transaction/TransactionCustomerInfo';
import TransactionPaymentMethod from './transaction/TransactionPaymentMethod';
import TransactionDetailCard from './transaction/TransactionDetailCard';
import TransactionFooter from './transaction/TransactionFooter';
import { hasProperty } from '@/utils/typeHelpers';
import { Button } from '@/components/ui/button'; // Import Button component

interface TransactionDetailsViewProps {
  id: string;
  onClose: () => void;
}

const TransactionDetailsView: React.FC<TransactionDetailsViewProps> = ({ id, onClose }) => {
  const { transaction, loading, error } = useTransactionDetails(id);

  // Loading state
  if (loading) {
    return <TransactionLoading onClose={onClose} />;
  }

  // Error state
  if (error) {
    return <TransactionError error={error} onClose={onClose} />;
  }

  // Not found state
  if (!transaction) {
    return <TransactionNotFound onClose={onClose} />;
  }

  // Get customer display info
  const getCustomerDisplayName = (): string => {
    // Check if customer is an object
    if (typeof transaction.customer === 'object' && transaction.customer !== null) {
      if (hasProperty(transaction.customer, 'name') && transaction.customer.name) {
        return String(transaction.customer.name);
      } 
      if (hasProperty(transaction.customer, 'email') && transaction.customer.email) {
        return String(transaction.customer.email);
      }
      if (hasProperty(transaction.customer, 'id') && transaction.customer.id) {
        return String(transaction.customer.id);
      }
    }
    
    // If it's a string, use it directly
    if (typeof transaction.customer === 'string') {
      return transaction.customer;
    }

    // Use customerEmail if available
    if (transaction.customerEmail) {
      return transaction.customerEmail;
    }

    // Default fallback
    return "Unknown Customer";
  };

  const customerDisplay = getCustomerDisplayName();

  // Check if we have payment method information
  const paymentMethod = transaction.paymentMethod || 
    (Array.isArray(transaction.payment_method_types) ? 
      transaction.payment_method_types[0] : 
      'Unknown');

  // Is this a KeepMore transaction?
  const isKeepMoreTransaction = 
    (transaction.is_keepmore_transaction === true || transaction.isKeepMoreTransaction === true) &&
    !(transaction.is_keepmore_transaction === false || transaction.isKeepMoreTransaction === false);

  return (
    <EntityDetailLayout 
      title= {null}
      onClose={onClose}
      footer={<TransactionFooter transaction={transaction} onClose={onClose} />}
    >
      <div className="flex items-start justify-between mb-8">
        <h1 className="font-semibold text-3xl">Transaction Details</h1>
      </div>
      <div className="space-y-6">
        {/* Amount and status */}
        <TransactionNetAmount transaction={transaction} />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Customer info */}
          <TransactionCustomerInfo 
            transaction={transaction} 
            customerDisplay={customerDisplay} 
          />
          
          {/* Payment method - fix the prop name */}
          <TransactionPaymentMethod displayPaymentMethod={paymentMethod} />
        </div>
        
        {/* Details section with fees, description, etc. */}
        <TransactionDetailCard 
          transaction={transaction}
          isKeepMoreTransaction={isKeepMoreTransaction}
        />
      </div>
    </EntityDetailLayout>
  );
};

export default TransactionDetailsView;
